import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { FormEvent, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAuthUserRestaurant,
  useEditProductOfferMutation,
  useGetSingleProductOfferQuery,
  ValidationError,
} from '@durma-soft/gros-sdk';
import { ProductOffer } from '@durma-soft/gros-sdk/dist/types/product-offers';

import { Modal } from '@/components/shared/modal';
import { Button } from '@/components/shared/shadcn-ui/button';
import { DialogFooter } from '@/components/shared/shadcn-ui/dialog';
import {
  ProductOfferFormData,
  ProductOfferFormFields,
} from '@/components/restaurant/product-offers/product-offer-form-fields';

interface EditProductOfferFormProps {
  offer: ProductOffer;
  closeModal: () => unknown;
}

const EditProductOfferForm = ({
  offer,
  closeModal,
}: EditProductOfferFormProps) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState<ProductOfferFormData>({
    name: offer.name,
    supplier_id: offer.supplier_id,
    price_per_unit: Number(offer.price_per_unit),
    unit: offer.unit,
    currency: offer.currency,
    image: offer.image,
  });

  const restaurant = useAuthUserRestaurant();
  const editProductOffer = useEditProductOfferMutation();

  const isValid = useMemo(() => {
    return (
      formData.name.length > 0 &&
      formData.supplier_id != null &&
      formData.price_per_unit != null &&
      formData.price_per_unit > 0 &&
      formData.unit.length > 0
    );
  }, [formData]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) return;
    try {
      await editProductOffer.mutateAsync({
        restaurant_id: restaurant.id,
        product_offer_id: offer.id,
        name: formData.name,
        supplier_id: formData.supplier_id ?? undefined,
        price_per_unit: formData.price_per_unit!,
        unit: formData.unit,
        currency: formData.currency,
        // string = unchanged existing image, File = replace, null = remove
        image: typeof formData.image === 'string' ? undefined : formData.image,
      });
      closeModal();
      toast.success(t('product-offers.edit-success-msg'));
    } catch (error) {
      if (error instanceof ValidationError) {
        return toast.error(error.message);
      }
      toast.error(t('product-offers.edit-error-msg'));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2">
      <ProductOfferFormFields formData={formData} setFormData={setFormData} />
      <DialogFooter className="mt-2">
        <Button
          type="submit"
          disabled={!isValid}
          isLoading={editProductOffer.isPending}
        >
          {t('product-offers.edit')}
        </Button>
      </DialogFooter>
    </form>
  );
};

export const EditProductOfferModal = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productOfferId } = useParams();

  const closeModal = () =>
    navigate('/restaurant/platform/product-offers', { replace: true });

  const restaurant = useAuthUserRestaurant();

  const { data } = useGetSingleProductOfferQuery({
    restaurant_id: restaurant.id,
    product_offer_id: Number(productOfferId),
  });

  return (
    <Modal
      isModalOpen
      closeModal={closeModal}
      title={t('product-offers.edit')}
      description={t('product-offers.edit-details')}
    >
      {data && <EditProductOfferForm offer={data} closeModal={closeModal} />}
    </Modal>
  );
};
